import React from "react";
import {
	Button,
	ButtonProps,
	ChildrenProps,
	ClassNameProps,
	Column,
	Grid,
	Heading,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { StudentNoteNoteType } from "../../../../Utilities";
import { StudentNoteFormat } from "../../../Format";
import { StudentNoteForm, StudentNoteTypeForm } from "../../../Forms";
import { animatedIconButtonStyles } from "../../_General";
import {
	StudentAcademicNoteIcon,
	StudentAttendanceNoteIcon,
	StudentBehaviorNoteIcon,
	StudentGeneralNoteIcon,
	StudentHealthNoteIcon,
	StudentIncidentNoteIcon,
	StudentInjuryNoteIcon,
	StudentMedicationNoteIcon,
	studentNoteTypeBackgroundColors,
	StudentSocialEmotionalNoteIcon,
} from "../Icon";
import {
	StudentAcademicNoteCreatePermissionRequired,
	StudentAttendanceNoteCreatePermissionRequired,
	StudentBehaviorNoteCreatePermissionRequired,
	StudentGeneralNoteCreatePermissionRequired,
	StudentHealthNoteCreatePermissionRequired,
	StudentIncidentNoteCreatePermissionRequired,
	StudentInjuryNoteCreatePermissionRequired,
	StudentMedicationNoteCreatePermissionRequired,
	StudentSocialEmotionalNoteCreatePermissionRequired,
} from "../PermissionRequired";

export type StudentNoteCreateModalControllerProps = Pick<ButtonProps, "variant" | "size" | "title"> &
	Omit<StudentNoteCreateModalProps, "ifRef" | "onClose" | "className" | "style"> &
	Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function StudentNoteCreateModalController({
	variant,
	size,
	title,
	className,
	style,
	children = (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
			<Icon.Plus size={16} /> Add a Note
		</Row>
	),
	...props
}: StudentNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant={variant} size={size} onClick={openModal} title={title} className={className} style={style}>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> &
	Omit<StudentNoteForm.ControlledCreateProps, "onSuccess" | "className" | "style" | "columnClassName" | "columnStyle">;

export function StudentNoteCreateModal({ ifRef, onClose, ...props }: StudentNoteCreateModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} style={{ width: "50rem" }}>
			<Modal.Header>
				<Heading.H2 noMargin>Add a Note</Heading.H2>
			</Modal.Header>
			<Modal.Body style={{ paddingBottom: "4rem" }}>
				<Grid tracks="auto-fill" minTrackWidth="9rem" style={{ alignItems: "center" }} gap="1rem">
					<StudentGeneralNoteCreatePermissionRequired>
						<StudentGeneralNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentGeneralNoteCreatePermissionRequired>
					<StudentAcademicNoteCreatePermissionRequired>
						<StudentAcademicNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentAcademicNoteCreatePermissionRequired>
					<StudentAttendanceNoteCreatePermissionRequired>
						<StudentAttendanceNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentAttendanceNoteCreatePermissionRequired>
					<StudentBehaviorNoteCreatePermissionRequired>
						<StudentBehaviorNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentBehaviorNoteCreatePermissionRequired>
					<StudentSocialEmotionalNoteCreatePermissionRequired>
						<StudentSocialEmotionalNoteCreateModalController
							onSuccess={onSuccess}
							style={{ width: "100%" }}
							{...props}
						/>
					</StudentSocialEmotionalNoteCreatePermissionRequired>
					<StudentHealthNoteCreatePermissionRequired>
						<StudentHealthNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentHealthNoteCreatePermissionRequired>
					<StudentIncidentNoteCreatePermissionRequired>
						<StudentIncidentNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentIncidentNoteCreatePermissionRequired>
					<StudentInjuryNoteCreatePermissionRequired>
						<StudentInjuryNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentInjuryNoteCreatePermissionRequired>
					<StudentMedicationNoteCreatePermissionRequired>
						<StudentMedicationNoteCreateModalController onSuccess={onSuccess} style={{ width: "100%" }} {...props} />
					</StudentMedicationNoteCreatePermissionRequired>
				</Grid>
			</Modal.Body>
		</Modal>
	);
}

export type StudentAcademicNoteCreateModalControllerProps = Omit<
	StudentAcademicNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentAcademicNoteCreateModalController({
	className,
	style,
	...props
}: StudentAcademicNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{ "--button-background-color": studentNoteTypeBackgroundColors.academic, ...style } as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentAcademicNoteIcon backgroundColor="transparent" />
					<span>Academic</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentAcademicNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentAcademicNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Academic.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentAcademicNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentAcademicNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentAcademicNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Academic)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Academic.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentAttendanceNoteCreateModalControllerProps = Omit<
	StudentAttendanceNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentAttendanceNoteCreateModalController({
	className,
	style,
	...props
}: StudentAttendanceNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{
						"--button-background-color": studentNoteTypeBackgroundColors.attendance,
						...style,
					} as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentAttendanceNoteIcon backgroundColor="transparent" />
					<span>Attendance</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentAttendanceNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentAttendanceNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Attendance.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentAttendanceNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentAttendanceNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentAttendanceNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Attendance)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Attendance.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentBehaviorNoteCreateModalControllerProps = Omit<
	StudentBehaviorNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentBehaviorNoteCreateModalController({
	className,
	style,
	...props
}: StudentBehaviorNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{ "--button-background-color": studentNoteTypeBackgroundColors.behavior, ...style } as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentBehaviorNoteIcon backgroundColor="transparent" />
					<span>Behavior</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentBehaviorNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentBehaviorNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Behavior.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentBehaviorNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentBehaviorNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentBehaviorNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Behavior)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Behavior.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentGeneralNoteCreateModalControllerProps = Omit<
	StudentGeneralNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentGeneralNoteCreateModalController({
	className,
	style,
	...props
}: StudentGeneralNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{ "--button-background-color": studentNoteTypeBackgroundColors.general, ...style } as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentGeneralNoteIcon backgroundColor="transparent" />
					<span>General</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentGeneralNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentGeneralNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.General.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentGeneralNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentGeneralNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentGeneralNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.General)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.General.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentHealthNoteCreateModalControllerProps = Omit<
	StudentHealthNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentHealthNoteCreateModalController({
	className,
	style,
	...props
}: StudentHealthNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={{ "--button-background-color": studentNoteTypeBackgroundColors.health, ...style } as React.CSSProperties}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentHealthNoteIcon backgroundColor="transparent" />
					<span>Health</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentHealthNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentHealthNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Health.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentHealthNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentHealthNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentHealthNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Health)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Health.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentIncidentNoteCreateModalControllerProps = Omit<
	StudentIncidentNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentIncidentNoteCreateModalController({
	className,
	style,
	...props
}: StudentIncidentNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{ "--button-background-color": studentNoteTypeBackgroundColors.incident, ...style } as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentIncidentNoteIcon backgroundColor="transparent" />
					<span>Incident</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentIncidentNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentIncidentNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Incident.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentIncidentNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentIncidentNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentIncidentNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Incident)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Incident.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentInjuryNoteCreateModalControllerProps = Omit<
	StudentInjuryNoteCreateModalProps,
	"ifRef" | "onClose" | "className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentInjuryNoteCreateModalController({
	className,
	style,
	...props
}: StudentInjuryNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={{ "--button-background-color": studentNoteTypeBackgroundColors.injury, ...style } as React.CSSProperties}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentInjuryNoteIcon backgroundColor="transparent" />
					<span>Injury</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentInjuryNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentInjuryNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Injury.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentInjuryNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentInjuryNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentInjuryNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Injury)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Injury.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentMedicationNoteCreateModalControllerProps = Omit<
	StudentMedicationNoteCreateModalProps,
	"className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentMedicationNoteCreateModalController({
	className,
	style,
	...props
}: StudentMedicationNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{
						"--button-background-color": studentNoteTypeBackgroundColors.medication,
						...style,
					} as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentMedicationNoteIcon backgroundColor="transparent" />
					<span>Medication</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentMedicationNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentMedicationNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.Medication.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentMedicationNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentMedicationNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentMedicationNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.Medication)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.Medication.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type StudentSocialEmotionalNoteCreateModalControllerProps = Omit<
	StudentSocialEmotionalNoteCreateModalProps,
	"className" | "style"
> &
	Partial<ClassNameProps & StyleProps>;

export function StudentSocialEmotionalNoteCreateModalController({
	className,
	style,
	...props
}: StudentSocialEmotionalNoteCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(animatedIconButtonStyles["animated-icon-button"], className)}
				style={
					{
						"--button-background-color": studentNoteTypeBackgroundColors.socialEmotional,
						...style,
					} as React.CSSProperties
				}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<StudentSocialEmotionalNoteIcon backgroundColor="transparent" />
					<span>Social/Emotional</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentSocialEmotionalNoteCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentSocialEmotionalNoteCreateModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	onSuccess?: () => void;
} & Omit<
		StudentNoteTypeForm.SocialEmotional.ControlledCreateProps,
		"onSuccess" | "className" | "style" | "columnClassName" | "columnStyle"
	>;

export function StudentSocialEmotionalNoteCreateModal({
	ifRef,
	onClose,
	onSuccess,
	...props
}: StudentSocialEmotionalNoteCreateModalProps) {
	const fullOnSuccess = React.useCallback(() => {
		onSuccess?.();
		onClose?.();
	}, [onClose, onSuccess]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentSocialEmotionalNoteIcon size={22} />
					<Heading.H2 noMargin>{StudentNoteFormat.Fields.noteType(StudentNoteNoteType.SocialEmotional)}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<StudentNoteTypeForm.SocialEmotional.ControlledCreate
					onSuccess={fullOnSuccess}
					columnStyle={{ width: "100%" }}
					{...props}
				/>
			</Modal.Body>
		</Modal>
	);
}
